// Floating-price formulas on a bunker enquiry (ledger row 132, "Paper Pricing
// groundwork"). One formula per product line and per side (sell / buy). The
// server works the price out from the uploaded Platts prices; this service
// only carries the formula and the result the server calculated -- nothing
// here computes a price.
//
// Errors are passed on exactly as axios raised them. Screens read the
// server's own sentence with ``extractApiError`` and the status from
// ``error.response``; wrapping them here would lose one or the other
// (rows 127 / 128).
//
// API: BUILD_CONTRACT_132, section B.

import axiosInstance from '../utils/axiosConfig';

const BASE_URL = '/api/fuel-enquiries';

// A reply without the part it promised is refused, never read as "nothing
// saved yet": an empty formula list would tell the trader no formula exists
// and send them to type it in again.
const expectShape = (data, key, what) => {
  const value = data ? data[key] : undefined;
  const ok = key === 'lines' || key === 'results' ? Array.isArray(value) : Boolean(value);
  if (!ok) {
    throw new Error(
      `The ${what} came back in a form this screen cannot read. Reload the page and try again.`,
    );
  }
  return data;
};

export const floatingFormulaService = {
  // ``{ lines: [{ index, product_id, label, unit_code, sell, buy }], can_edit }``.
  // ``buy`` is absent for a customer trader: the server never sends it.
  list: async (enquiryId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${enquiryId}/floating-formulas/`,
    );
    return expectShape(response.data, 'lines', 'floating formulas');
  },

  // Saves one line's formula for one side. ``{ formula, result }``.
  save: async (enquiryId, body) => {
    const response = await axiosInstance.put(
      `${BASE_URL}/enquiries/${enquiryId}/floating-formulas/`,
      body,
    );
    return response.data;
  },

  // ``product_id`` goes out as the word "null" for an enquiry without product
  // rows. axios drops a null query parameter altogether, and a delete that
  // does not name its line must never be left for the server to guess.
  remove: async (enquiryId, { productId, side }) => {
    await axiosInstance.delete(
      `${BASE_URL}/enquiries/${enquiryId}/floating-formulas/`,
      { params: { product_id: productId == null ? 'null' : productId, side } },
    );
  },

  // Same body as ``save``; never saves. A formula that cannot be priced still
  // answers 200 with ``status: "refused"`` and the reason, so the window can
  // show it while the trader types.
  preview: async (enquiryId, body) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${enquiryId}/floating-formulas/preview/`,
      body,
    );
    return expectShape(response.data, 'result', 'price preview');
  },

  // Active benchmarks only, each saying whether a formula may use it.
  benchmarks: async () => {
    const response = await axiosInstance.get(`${BASE_URL}/floating-formula-benchmarks/`);
    return expectShape(response.data, 'results', 'benchmark list');
  },

  // The task the hedging desk gets (or already has) for this enquiry's
  // fixed-vs-floating exposure. Refused (403) for customer traders.
  hedgingTaskPreview: async (enquiryId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${enquiryId}/hedging-task-preview/`,
    );
    return expectShape(response.data, 'content', 'hedging task preview');
  },
};

export default floatingFormulaService;
